import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Bell,
  CalendarDays,
  Ellipsis,
  Grid2x2,
  MessageSquareText,
  Package,
  ReceiptText,
  Settings,
  Users,
  LayoutDashboard,
  Bot,
  ShieldCheck,
  ChevronDown,
  Search,
  X,
  CreditCard,
  Tags,
  ListChecks,
  Zap,
} from 'lucide-react'
import { useLangPrefix } from '../../hooks/useLangPrefix'
import useTenantVertical from '../../hooks/useTenantVertical'
import { makeVT } from '../../utils/verticals'
import LanguageSwitcher from './LanguageSwitcher'
import useManagerStore from '../../store/managerStore'
import { useAuthStore } from '../../store/authStore'
import { canAccess, normalizeRole, RBAC_MODULES, TENANT_FEATURE_FLAGS } from '../../lib/rbac'
import { useFeatureFlag } from '../../lib/useFeatureFlag'
import BillingStatusBanners from './BillingStatusBanners'
import AnnouncementBanner from './AnnouncementBanner'
import { useBellNotifications } from '../../hooks/useBellNotifications'
import { findNavItem } from './navConfig'


const primaryTabs = [
  {
    labelKey: 'nav.dashboard',
    route: '/dashboard',
    Icon: LayoutDashboard,
  },
  {
    labelKey: 'nav.conversations',
    route: '/inbox',
    Icon: MessageSquareText,
  },
  {
    labelKey: 'nav.transactions',
    route: '/bookings',
    Icon: CalendarDays,
  },
  {
    labelKey: 'nav.customerNav',
    route: '/customers',
    subItems: [
      { labelKey: 'nav.customers', route: '/customers' },
      { labelKey: 'nav.followers', route: '/followers' },
    ],
    Icon: Users,
  },
]

// Mobile keeps its own flat drawer (no regrouping, Q-F12); the FAQ entry follows
// the desktop config's availability flag.
const faqNavItem = findNavItem('faq')
const playbookNavItem = findNavItem('playbook')
const quickAnswerNavItem = findNavItem('quick-answers')

const drawerItems = [
  { key: 'automations', labelKey: 'nav.automations', Icon: Zap, path: '/automations' },
  { key: 'products', labelKey: 'nav.products', Icon: Package, path: '/products' },
  { key: 'tags', labelKey: 'nav.tags', Icon: Tags, path: '/tags' },
  { key: 'custom-statuses', labelKey: 'nav.customStatus', Icon: ListChecks, path: '/custom-statuses' },
  { key: 'orders', labelKey: 'nav.orders', Icon: ReceiptText, path: '/orders' },
  { key: 'bot-config', labelKey: 'nav.botConfig', Icon: Bot, path: '/bot-config' },
  ...(faqNavItem?.available
    ? [{ key: faqNavItem.key, labelKey: faqNavItem.labelKey, Icon: faqNavItem.icon, path: faqNavItem.route }]
    : []),
  ...(playbookNavItem?.available
    ? [{ key: playbookNavItem.key, labelKey: playbookNavItem.labelKey, Icon: playbookNavItem.icon, path: playbookNavItem.route }]
    : []),
  ...(quickAnswerNavItem?.available
    ? [{ key: quickAnswerNavItem.key, labelKey: quickAnswerNavItem.labelKey, Icon: quickAnswerNavItem.icon, path: quickAnswerNavItem.route }]
    : []),
  { key: 'admin-permissions', labelKey: 'nav.adminPermissions', Icon: ShieldCheck, path: '/admin-permissions' },
  { key: 'billing', labelKey: 'nav.billing', Icon: CreditCard, path: '/billing' },
  { key: 'settings', labelKey: 'nav.settings', Icon: Settings, path: '/settings' },
]

const moduleByRoute = {
  '/dashboard': RBAC_MODULES.dashboard,
  '/inbox': RBAC_MODULES.conversations,
  '/bookings': RBAC_MODULES.bookings,
  '/customers': RBAC_MODULES.contacts,
  '/followers': RBAC_MODULES.contacts,
  '/tags': RBAC_MODULES.contactTags,
  '/custom-statuses': RBAC_MODULES.customStatus,
  '/products': RBAC_MODULES.products,
  '/orders': RBAC_MODULES.orders,
  '/bot-config': RBAC_MODULES.botConfig,
  '/faq': RBAC_MODULES.botConfig,
  '/playbook': RBAC_MODULES.botConfig,
  '/quick-answers': RBAC_MODULES.botConfig,
  '/admin-permissions': RBAC_MODULES.adminPermissions,
  '/billing': RBAC_MODULES.billing,
  '/settings': RBAC_MODULES.settings,
  '/automations': RBAC_MODULES.automation,
}

// Tenant-level GrowthBook gate, checked alongside RBAC above. Only routes with
// a per-tenant on/off toggle appear here.
const featureFlagByPath = {
  '/tags': TENANT_FEATURE_FLAGS.contact_tags,
  '/custom-statuses': TENANT_FEATURE_FLAGS.custom_status,
  '/orders': TENANT_FEATURE_FLAGS.orders,
  '/bookings': TENANT_FEATURE_FLAGS.bookings,
  '/faq': TENANT_FEATURE_FLAGS.faq,
  '/reply-log': TENANT_FEATURE_FLAGS.bot_logs,
  '/playbook': TENANT_FEATURE_FLAGS.playbook,
  '/quick-answers': TENANT_FEATURE_FLAGS.quick_answer,
}

export default function AppShell({ title, leftAction, rightAction, children }) {
  const { t } = useTranslation()
  const tenantVertical = useTenantVertical()
  const vt = makeVT(t, tenantVertical)
  const navigate = useNavigate()
  const location = useLocation()
  const { prefix } = useLangPrefix()
  const tenantId = useManagerStore((s) => s.tenantId)
  const storeRole = useManagerStore((s) => s.role)
  const branches = useAuthStore((s) => s.branches)
  const selectBranch = useAuthStore((s) => s.selectBranch)
  const [showMore, setShowMore] = useState(false)
  const role = normalizeRole(storeRole)
  const tagsFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.contact_tags, false)
  const customStatusFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.custom_status, false)
  const ordersFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.orders, false)
  const bookingsFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.bookings, false)
  const faqFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.faq, false)
  const botLogsFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.bot_logs, false)
  const playbookFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.playbook, false)
  const quickAnswerFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.quick_answer, false)
  const featureFlagValues = {
    [TENANT_FEATURE_FLAGS.contact_tags]: tagsFeatureEnabled,
    [TENANT_FEATURE_FLAGS.custom_status]: customStatusFeatureEnabled,
    [TENANT_FEATURE_FLAGS.orders]: ordersFeatureEnabled,
    [TENANT_FEATURE_FLAGS.bookings]: bookingsFeatureEnabled,
    [TENANT_FEATURE_FLAGS.faq]: faqFeatureEnabled,
    [TENANT_FEATURE_FLAGS.bot_logs]: botLogsFeatureEnabled,
    [TENANT_FEATURE_FLAGS.playbook]: playbookFeatureEnabled,
    [TENANT_FEATURE_FLAGS.quick_answer]: quickAnswerFeatureEnabled,
  }
  const visiblePrimaryTabs = primaryTabs
    .map((tab) => {
      if (!tab.subItems) return tab
      return {
        ...tab,
        subItems: tab.subItems.filter((subItem) => {
          const flagKey = featureFlagByPath[subItem.route]
          if (flagKey && !featureFlagValues[flagKey]) return false
          const moduleName = moduleByRoute[subItem.route]
          if (!moduleName) return true
          return canAccess(role, moduleName)
        }),
      }
    })
    .filter((tab) => {
      const flagKey = featureFlagByPath[tab.route]
      if (flagKey && !featureFlagValues[flagKey]) return false
      const moduleName = moduleByRoute[tab.route]
      if (!moduleName) return true
      return canAccess(role, moduleName)
    })
  const visibleDrawerItems = drawerItems.filter((item) => {
    const flagKey = featureFlagByPath[item.path]
    if (flagKey && !featureFlagValues[flagKey]) return false
    const moduleName = moduleByRoute[item.path]
    if (!moduleName) return true
    return canAccess(role, moduleName)
  })

  const [showBranchPicker, setShowBranchPicker] = useState(false)
  const [branchSearch, setBranchSearch] = useState('')

  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef(null)
  const {
    notifications: displayedNotifications,
    unreadCount: displayedUnreadCount,
    refresh: refreshNotifications,
    markAllRead: handleMarkAllRead,
    remove: handleDeleteNotif,
    open: openBellNotification,
  } = useBellNotifications()

  const openNotification = (notification) => {
    setNotifOpen(false)
    openBellNotification(notification)
  }

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function routePath(path) {
    return `${prefix}${path}`
  }

  function isRouteActive(path) {
    const full = routePath(path)
    return location.pathname === full || location.pathname.startsWith(`${full}/`)
  }

  const defaultRight = (
    <div ref={notifRef} style={{ position: 'relative' }}>
      <button
        onClick={() =>
          setNotifOpen((o) => {
            const next = !o
            if (next) refreshNotifications('bell-open')
            return next
          })
        }
        style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', position: 'relative', color: 'white' }}
      >
        <Bell size={22} />
        {displayedUnreadCount > 0 && (
          <span style={{ position: 'absolute', top: 7, right: 7, minWidth: 16, height: 16, background: '#f97316', borderRadius: '50%', border: '2px solid #1535c8', fontSize: 10, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: '0 2px' }}>
            {displayedUnreadCount > 9 ? '9+' : displayedUnreadCount}
          </span>
        )}
      </button>
      {notifOpen && (
        <div style={{ position: 'absolute', right: 0, top: 48, width: 300, background: 'white', border: '1px solid #e4e4e7', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', zIndex: 999, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #f4f4f5' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#3f3f46' }}>{t('notifications.title')}</span>
            {displayedUnreadCount > 0 && (
              <button onClick={handleMarkAllRead} style={{ fontSize: 12, color: '#1535c8', background: 'none', border: 'none', cursor: 'pointer' }}>
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {displayedNotifications.length === 0 ? (
              <p style={{ fontSize: 14, color: '#a1a1aa', textAlign: 'center', padding: '24px 0' }}>{t('notifications.empty')}</p>
            ) : displayedNotifications.map((n) => (
              <div
                key={n.id}
                onClick={() => openNotification(n)}
                style={{ position: 'relative', padding: '12px 16px', cursor: 'pointer', background: n.read ? 'white' : '#fff7ed', borderBottom: '1px solid #fafafa' }}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteNotif(n.id) }}
                  title={t('notifications.delete')}
                  style={{ position: 'absolute', top: 8, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#d4d4d8', fontSize: 12 }}
                >✕</button>
                <p style={{ fontSize: 14, fontWeight: n.read ? 400 : 600, color: n.read ? '#52525b' : '#18181b', paddingRight: 16, margin: 0 }}>{n.title}</p>
                <p style={{ fontSize: 12, color: '#a1a1aa', marginTop: 2, marginBottom: 0 }}>{n.body}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => { setNotifOpen(false); navigate(`${prefix}/notifications`) }}
            style={{ display: 'block', width: '100%', padding: '10px 16px', background: 'white', border: 'none', borderTop: '1px solid #f4f4f5', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#1535c8', textAlign: 'center' }}
          >
            {t('notifications.viewAll')} →
          </button>
        </div>
      )}
    </div>
  )

  const moreActive =
    showMore || visibleDrawerItems.some((i) => isRouteActive(i.path))

  const activeGroupTab = visiblePrimaryTabs.find(tab =>
    tab.subItems &&
    (isRouteActive(tab.route) || tab.subItems.some(s => isRouteActive(s.route)))
  )
  const activeBranch =
    branches.find((branch) => branch?.tenant_id && branch.tenant_id === tenantId) ??
    branches[0] ??
    null
  const normalizedSearch = branchSearch.trim().toLowerCase()
  const filteredBranches = !normalizedSearch
    ? branches
    : branches.filter((branch) => {
      const name = (branch?.name || '').toLowerCase()
      const address = (branch?.address || '').toLowerCase()
      return name.includes(normalizedSearch) || address.includes(normalizedSearch)
    })

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f2f4f8' }}>
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 30,
        height: 62,
        background: '#1535c8',
        display: 'flex', alignItems: 'center',
        paddingLeft: 6, paddingRight: 6,
      }}>
        {leftAction ?? <div style={{ width: 44, flexShrink: 0 }} />}
        <span style={{
          color: 'white',
          fontWeight: 700,
          fontSize: 19,
          flex: 1,
          minWidth: 0,
          textAlign: 'center',
          letterSpacing: -0.4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          paddingLeft: 6,
          paddingRight: 6,
        }}>
          {title}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <div style={{ transform: 'scale(0.9)', transformOrigin: 'center' }}>
            <LanguageSwitcher />
          </div>
          {rightAction ?? defaultRight}
        </div>
      </header>

      <main style={{
        flex: 1, overflowY: 'auto',
        paddingTop: 62,
        paddingBottom: 96,
        paddingLeft: 14, paddingRight: 14,
      }}>
        {branches.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setBranchSearch('')
              setShowBranchPicker(true)
            }}
            style={{
              width: '100%',
              marginTop: 12,
              marginBottom: 8,
              borderRadius: 14,
              border: '1px solid #e5e7eb',
              background: '#f7f8fa',
              padding: '10px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              color: '#374151',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{
                width: 28, height: 28, borderRadius: 9999, background: '#e5e7eb',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Grid2x2 size={14} color="#6b7280" />
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                {activeBranch?.name || t('branchGate.switchBranch')}
              </span>
            </span>
            <ChevronDown size={16} color="#6b7280" />
          </button>
        )}
        <BillingStatusBanners className="mb-3" />
        <AnnouncementBanner className="mb-3" />
        {children}
      </main>

      <nav style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 24px)',
        maxWidth: 500,
        background: 'white',
        borderRadius: 24,
        boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '6px 4px',
        zIndex: 100,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}>
        {visiblePrimaryTabs.map((tab) => {
          const active = tab.subItems
            ? isRouteActive(tab.route) || tab.subItems.some(s => isRouteActive(s.route))
            : isRouteActive(tab.route)
          return (
            <button
              key={tab.route}
              onClick={() =>
                navigate(routePath(tab.route))
              }
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                background: active ? '#eef1fc' : 'transparent',
                borderRadius: 14,
                border: 'none',
                cursor: 'pointer',
                padding: '6px 10px',
              }}
            >
              <tab.Icon size={18} strokeWidth={1.9} color={active ? '#1535c8' : '#9ca3af'} />
              <span style={{
                fontSize: 10,
                fontWeight: active ? 700 : 500,
                color: active ? '#1535c8' : '#999',
                letterSpacing: -0.1,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}>
                {vt(tab.labelKey)}
              </span>
            </button>
          )
        })}

        {visibleDrawerItems.length > 0 && (
          <button
            onClick={() => setShowMore(true)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              background: moreActive ? '#eef1fc' : 'transparent',
              borderRadius: 14,
              border: 'none',
              cursor: 'pointer',
              padding: '6px 10px',
            }}
          >
            <Ellipsis size={18} color={moreActive ? '#1535c8' : '#9ca3af'} />
            <span style={{
              fontSize: 10,
              fontWeight: moreActive ? 700 : 500,
              color: moreActive ? '#1535c8' : '#999',
              letterSpacing: -0.1,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}>
              {t('common.more')}
            </span>
          </button>
        )}
      </nav>

      {/* Sub-items strip — shown above the tab bar when a grouped tab is active */}
      {activeGroupTab && (
        <div style={{
          position: 'fixed',
          bottom: 82,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 4,
          background: 'white',
          borderRadius: 16,
          padding: '4px 6px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
          zIndex: 99,
        }}>
          {activeGroupTab.subItems.map(sub => {
            const subActive = isRouteActive(sub.route)
            return (
              <button
                key={sub.route}
                onClick={() => navigate(routePath(sub.route))}
                style={{
                  fontSize: 12,
                  fontWeight: subActive ? 700 : 500,
                  color: subActive ? '#1535c8' : '#999',
                  background: subActive ? '#eef1fc' : 'transparent',
                  border: 'none',
                  borderRadius: 10,
                  padding: '4px 14px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {vt(sub.labelKey)}
              </button>
            )
          })}
        </div>
      )}

      {/* More drawer */}
      {showMore && visibleDrawerItems.length > 0 && (
        <>
          <div
            onClick={() => setShowMore(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 }}
          />
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            background: 'white',
            borderRadius: '16px 16px 0 0',
            padding: '16px 0 24px',
            zIndex: 201,
          }}>
            <div style={{ width: 40, height: 4, background: '#e0e3f0', borderRadius: 2, margin: '0 auto 16px' }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: '#666', paddingLeft: 20, marginBottom: 12 }}>{t('common.more')}</div>
            {visibleDrawerItems.map(item => {
              const active = isRouteActive(item.path)
              return (
                <div
                  key={item.key}
                  onClick={() => {
                    navigate(routePath(item.path))
                    setShowMore(false)
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 24px',
                    fontSize: 15,
                    color: active ? '#1535c8' : '#111',
                    background: active ? '#f0f3ff' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <item.Icon size={18} color={active ? '#1535c8' : '#6b7280'} />
                  {vt(item.labelKey)}
                </div>
              )
            })}
          </div>
        </>
      )}

      {showBranchPicker && (
        <>
          <div
            onClick={() => setShowBranchPicker(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 220 }}
          />
          <div style={{
            position: 'fixed', top: 76, left: 12, right: 12, bottom: 12,
            background: 'white',
            borderRadius: 16,
            border: '1px solid #e5e7eb',
            padding: 14,
            zIndex: 221,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
              <button
                type="button"
                onClick={() => setShowBranchPicker(false)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                value={branchSearch}
                onChange={(e) => setBranchSearch(e.target.value)}
                placeholder={t('branchGate.searchPlaceholder')}
                style={{
                  width: '100%',
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  outline: 'none',
                  padding: '11px 12px 11px 36px',
                  fontSize: 16,
                }}
              />
            </div>

            <div style={{ marginTop: 14, marginBottom: 10, fontSize: 12, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {t('branchGate.allAccounts')}
            </div>

            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 2 }}>
              {filteredBranches.map((branch) => {
                const disabled = !branch?.tenant_id
                const isActive = !!tenantId && branch?.tenant_id === tenantId
                const initial = (branch?.name || '?').trim().charAt(0).toUpperCase()
                return (
                  <button
                    key={branch.id}
                    type="button"
                    disabled={disabled}
                    onClick={async () => {
                      if (disabled) return
                      const result = await selectBranch(branch.id)
                      if (result?.ok) {
                        setShowBranchPicker(false)
                      }
                    }}
                    style={{
                      width: '100%',
                      borderRadius: 14,
                      border: `1px solid ${isActive ? '#1535c8' : '#e5e7eb'}`,
                      background: disabled ? '#f9fafb' : 'white',
                      padding: '12px 14px',
                      textAlign: 'left',
                      opacity: disabled ? 0.6 : 1,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{
                        width: 34, height: 34, borderRadius: 9999, background: '#f3f4f6', color: '#6b7280',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                      }}>
                        {initial}
                      </span>
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ display: 'block', fontSize: 18, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {branch?.name || '-'}
                        </span>
                        <span style={{ display: 'block', fontSize: 14, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {branch?.address || t('branchGate.noAddress')}
                        </span>
                      </span>
                    </div>
                  </button>
                )
              })}
              {filteredBranches.length === 0 && (
                <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, padding: '12px 8px' }}>
                  {t('branchGate.noMatchingBranches')}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
